import { baseUrl } from "../utilities/configurations";
import { useNavigate } from "react-router-dom";
import apiRequest from "../utilities/request";
import PropTypes from "prop-types";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";

const LoaMedicalTestContext = createContext();

export function useLoaMedicalTestContext() {
  return useContext(LoaMedicalTestContext);
}

export function LoaMedicalTestProvider(props) {
  const initForm = {
    requestingDoctorName: null,
    selectedPolicy: null,
    hospitalName: null,
    chiefComplaint: "",
    attachment: null,
    procedures: [],
    diagnosis: null,
    conformeAccepted: false,
  };

  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [displayAlert, setDisplayAlert] = useState(false);
  const [allProcedures, setAllProcedures] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [allHospitals, setAllHospitals] = useState([]);
  const [allDiagnosis, setAllDiagnosis] = useState([]);
  const [formData, setFormData] = useState(initForm);
  const [currentStep, setCurrentStep] = useState(1);
  const [errorField, setErrorField] = useState("");
  const [procedures, setProcedures] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  const [loadState, setLoadState] = useState("");
  const [diagnoses, setDiagnoses] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [doctors, setDoctors] = useState([]);

  const [isGettingProcedures, setIsGettingProcedures] = useState(false);
  const [isGettingHospitals, setIsGettingHospitals] = useState(false);
  const [isGettingDiagnoses, setIsGettingDiagnoses] = useState(false);
  const [isGettingDoctors, setIsGettingDoctors] = useState(false);
  const [isConformeOpen, setIsConformeOpen] = useState(false);
  const [openField, setOpenField] = useState("");

  const [viewModalShown, setViewModalShown] = useState(false);
  const [base64File, setBase64File] = useState(null);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const [isDropdownLoadFailed, setIsDropdownLoadFailed] = useState(false);
  const [dropdownErrorField, setDropdownErrorField] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    getHospitals();
    getDiagnoses();
  }, []);

  const handleBack = (e) => {
    navigate(-1);
  };

  const clearDropdownError = () => {
    setIsDropdownLoadFailed(false);
    setDropdownErrorField("");
  };

  const setDropdownError = (field) => {
    setIsDropdownLoadFailed(true);
    setDropdownErrorField(field);
  };

  const getHospitals = async () => {
    try {
      setIsGettingHospitals(true);
      const res = await apiRequest(baseUrl + `/LOA/Providers`, "GET");
      if (!res.error) {
        setAllHospitals(res.data || []);
        clearDropdownError();
      } else {
        setDropdownError("hospitals");
      }
    } catch (error) {
      console.log(error);
      setDropdownError("hospitals");
    } finally {
      setIsGettingHospitals(false);
    }
  };

  const getProcedures = async (code) => {
    try {
      setIsGettingProcedures(true);
      const res = await apiRequest(
        baseUrl + `/LOA/procedures?providerCode=${code}`,
        "GET"
      );
      if (!res.error) {
        setAllProcedures(res.data || []);
        clearDropdownError();
      } else {
        setDropdownError("procedures");
      }
    } catch (error) {
      console.log(error);
      setDropdownError("procedures");
    } finally {
      setIsGettingProcedures(false);
    }
  };

  const getDiagnoses = async () => {
    try {
      setIsGettingDiagnoses(true);
      const res = await apiRequest(baseUrl + `/LOA/common-icd`, "GET");
      if (!res.error) {
        setAllDiagnosis(res.data || []);
        clearDropdownError();
      } else {
        setDropdownError("diagnoses");
      }
    } catch (error) {
      console.log(error);
      setDropdownError("diagnoses");
    } finally {
      setIsGettingDiagnoses(false);
    }
  };

  const reloadDropdown = () => {
    if (dropdownErrorField === "hospitals") {
      getHospitals();
    } else if (dropdownErrorField === "procedures") {
      getProcedures();
    } else if (dropdownErrorField === "diagnoses") {
      getDiagnoses();
    }
  };

  const data = useMemo(
    () => ({
      setIsGettingProcedures,
      setIsGettingHospitals,
      setIsGettingDiagnoses,
      setIsGettingDoctors,
      setShowUploadModal,
      setSelectedPolicy,
      setIsConformeOpen,
      setViewModalShown,
      setAllProcedures,
      setDisplayAlert,
      setAllDiagnosis,
      setErrorMessage,
      setAllHospitals,
      setIsCameraOpen,
      reloadDropdown,
      setCurrentStep,
      setErrorField,
      setProcedures,
      setAllDoctors,
      getProcedures,
      setBase64File,
      setHospitals,
      setLoadState,
      setOpenField,
      getHospitals,
      setDiagnoses,
      getDiagnoses,
      setFormData,
      handleBack,
      setDoctors,
      navigate,

      isDropdownLoadFailed,
      isGettingProcedures,
      dropdownErrorField,
      isGettingHospitals,
      isGettingDiagnoses,
      isGettingDoctors,
      showUploadModal,
      isConformeOpen,
      viewModalShown,
      selectedPolicy,
      allProcedures,
      isCameraOpen,
      displayAlert,
      errorMessage,
      allHospitals,
      allDiagnosis,
      currentStep,
      base64File,
      errorField,
      procedures,
      allDoctors,
      loadState,
      diagnoses,
      hospitals,
      openField,
      formData,
      doctors,
    }),
    [
      isDropdownLoadFailed,
      isGettingProcedures,
      dropdownErrorField,
      isGettingHospitals,
      isGettingDiagnoses,
      isGettingDoctors,
      showUploadModal,
      isConformeOpen,
      viewModalShown,
      selectedPolicy,
      allProcedures,
      isCameraOpen,
      errorMessage,
      displayAlert,
      allHospitals,
      allDiagnosis,
      currentStep,
      base64File,
      errorField,
      procedures,
      allDoctors,
      openField,
      diagnoses,
      hospitals,
      loadState,
      formData,
      doctors,
    ]
  );

  return (
    <LoaMedicalTestContext.Provider value={data}>
      {props.children}
    </LoaMedicalTestContext.Provider>
  );
}

LoaMedicalTestProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
